/**
 * Suggestion mode ("track changes" subset, Google-Docs-style):
 *
 *  - While ON, text typed by the user is NOT plain text: it gets the
 *    `suggestionInsert` mark (an `appendTransaction` plugin marks every
 *    inserted range). Contiguous typing extends the same suggestion; a new
 *    burst elsewhere opens a new one (callback → sidecar thread).
 *  - While ON, Backspace/Delete do not remove text: the range gets the
 *    `suggestionDelete` mark (struck-through proposal). Deleting inside your
 *    own pending insertion really deletes it (there is nothing to review).
 *  - Accept/reject lives in `suggestion-ops.ts` and is driven from the panel.
 *
 * Scope (documented): plain-text insert/delete runs. Structural suggestions
 * (splitting/merging blocks, node type changes) apply directly even in
 * suggestion mode — the 90/20 of a review flow.
 */
import { type Editor, Extension } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection, type Transaction } from "@tiptap/pm/state";
import type { MarkType, Node as PMNode } from "@tiptap/pm/model";
import { generateId } from "@folio/shared";
import { SUGGESTION_DELETE_MARK, SUGGESTION_INSERT_MARK } from "./marks";

/** Meta flag: transactions produced by our own ops (accept/reject/remote sync)
 * must never be re-interpreted as user suggestions. */
export const SUGGESTION_SKIP_META = "suggestion-skip";

export interface SuggestionModeStorage {
  enabled: boolean;
}

export interface SuggestionModeOptions {
  /** A NEW pending insertion appeared (user typed a fresh run while ON). */
  onSuggestInsert?: (suggestionId: string) => void;
  /** A NEW deletion proposal appeared (Backspace/Delete or toolbar while ON). */
  onSuggestDelete?: (suggestionId: string, text: string) => void;
}

interface Range {
  from: number;
  to: number;
}

/** Id of a suggestion mark of `type` touching the range (inside or adjacent). */
function adjacentSuggestionId(
  doc: PMNode,
  from: number,
  to: number,
  type: MarkType,
): string | null {
  const probes: (PMNode | null | undefined)[] = [];
  try {
    probes.push(doc.resolve(from).nodeBefore, doc.resolve(to).nodeAfter);
    if (to > from) {
      probes.push(doc.nodeAt(from));
    }
  } catch {
    return null;
  }
  for (const node of probes) {
    const mark = node?.marks.find((m) => m.type === type);
    const id: unknown = mark?.attrs["suggestionId"];
    if (typeof id === "string" && id.length > 0) {
      return id;
    }
  }
  return null;
}

/** Ranges of text inserted by `transactions`, mapped to the final doc. */
function insertedRanges(transactions: readonly Transaction[]): Range[] {
  const ranges: Range[] = [];
  for (let i = 0; i < transactions.length; i++) {
    const tr = transactions[i];
    if (!tr?.docChanged) {
      continue;
    }
    tr.steps.forEach((step, stepIndex) => {
      step.getMap().forEach((_fromA, _toA, fromB, toB) => {
        if (toB <= fromB) {
          return;
        }
        let from = fromB;
        let to = toB;
        // Map the range through the remaining steps of this transaction…
        for (let s = stepIndex + 1; s < tr.steps.length; s++) {
          const map = tr.steps[s]?.getMap();
          if (map) {
            from = map.map(from, 1);
            to = map.map(to, -1);
          }
        }
        // …and through every later transaction of the batch.
        for (let j = i + 1; j < transactions.length; j++) {
          for (const later of transactions[j]?.steps ?? []) {
            const map = later.getMap();
            from = map.map(from, 1);
            to = map.map(to, -1);
          }
        }
        if (to > from) {
          ranges.push({ from, to });
        }
      });
    });
  }
  return ranges;
}

/**
 * Marks `[from, to)` as a deletion proposal (text stays, struck through).
 * Reuses an adjacent proposal's id when extending one; otherwise creates a new
 * suggestion id and notifies via `onSuggestDelete`. Returns the id used, or
 * null when the range was empty.
 */
export function suggestDeleteRange(
  editor: Editor,
  from: number,
  to: number,
  onSuggestDelete?: (suggestionId: string, text: string) => void,
): string | null {
  if (to <= from) {
    return null;
  }
  const deleteType = editor.schema.marks[SUGGESTION_DELETE_MARK];
  if (!deleteType) {
    return null;
  }
  const { doc } = editor.state;
  const existing = adjacentSuggestionId(doc, from, to, deleteType);
  const id = existing ?? generateId();
  const { tr } = editor.state;
  tr.addMark(from, to, deleteType.create({ suggestionId: id }));
  tr.setMeta(SUGGESTION_SKIP_META, true);
  editor.view.dispatch(tr);
  if (!existing) {
    onSuggestDelete?.(id, doc.textBetween(from, to, " "));
  }
  return id;
}

export const SuggestionMode = Extension.create<SuggestionModeOptions, SuggestionModeStorage>({
  name: "suggestionMode",

  addOptions() {
    return { onSuggestInsert: undefined, onSuggestDelete: undefined };
  },

  addStorage() {
    return { enabled: false };
  },

  addKeyboardShortcuts() {
    const handleDeletion = (direction: -1 | 1): boolean => {
      if (!this.storage.enabled) {
        return false;
      }
      const { state, view } = this.editor;
      const { selection, doc } = state;
      const insertType = state.schema.marks[SUGGESTION_INSERT_MARK];
      const deleteType = state.schema.marks[SUGGESTION_DELETE_MARK];
      if (!insertType || !deleteType) {
        return false;
      }

      let { from } = selection;
      let { to } = selection;
      if (selection.empty) {
        if (direction === -1) {
          if (from <= 1) {
            return false;
          }
          from -= 1;
        } else {
          if (to >= doc.content.size - 1) {
            return false;
          }
          to += 1;
        }
        // Only convert plain character deletions; let structural joins
        // (line start/end, node boundaries) behave normally.
        if (doc.textBetween(from, to, " ").length !== 1) {
          return false;
        }
      }

      // Deleting your OWN pending insertion really deletes it — there is
      // nothing for a reviewer to act on.
      if (doc.rangeHasMark(from, to, insertType)) {
        return false;
      }

      // Already proposed for deletion → just walk the cursor past it.
      if (doc.rangeHasMark(from, to, deleteType)) {
        const target = direction === -1 ? from : to;
        const tr = state.tr.setSelection(TextSelection.create(doc, target));
        tr.setMeta(SUGGESTION_SKIP_META, true);
        view.dispatch(tr);
        return true;
      }

      suggestDeleteRange(this.editor, from, to, this.options.onSuggestDelete);
      const cursor = direction === -1 ? from : to;
      const tr = this.editor.state.tr.setSelection(
        TextSelection.create(this.editor.state.doc, cursor),
      );
      tr.setMeta(SUGGESTION_SKIP_META, true);
      view.dispatch(tr);
      return true;
    };

    return {
      Backspace: () => handleDeletion(-1),
      Delete: () => handleDeletion(1),
    };
  },

  addProseMirrorPlugins() {
    const { storage } = this;
    const { options } = this;
    return [
      new Plugin({
        key: new PluginKey("suggestion-mode"),
        appendTransaction: (transactions, _oldState, newState) => {
          if (!storage.enabled) {
            return null;
          }
          if (transactions.some((tr) => tr.getMeta(SUGGESTION_SKIP_META) === true)) {
            return null;
          }
          const ranges = insertedRanges(transactions);
          if (ranges.length === 0) {
            return null;
          }
          const insertType = newState.schema.marks[SUGGESTION_INSERT_MARK];
          if (!insertType) {
            return null;
          }
          const { tr } = newState;
          tr.setMeta(SUGGESTION_SKIP_META, true);
          const newIds: string[] = [];
          let changed = false;
          for (const range of ranges) {
            const existing = adjacentSuggestionId(newState.doc, range.from, range.to, insertType);
            const id = existing ?? generateId();
            if (!existing) {
              newIds.push(id);
            }
            tr.addMark(range.from, range.to, insertType.create({ suggestionId: id }));
            changed = true;
          }
          if (!changed) {
            return null;
          }
          for (const id of newIds) {
            options.onSuggestInsert?.(id);
          }
          return tr;
        },
      }),
    ];
  },
});

// Typed access to `editor.storage.suggestionMode` (TipTap storage augmentation).
declare module "@tiptap/core" {
  interface Storage {
    suggestionMode: SuggestionModeStorage;
  }
}

/** Toggle suggestion mode on a live editor. */
export function setSuggestionModeEnabled(editor: Editor, enabled: boolean): void {
  editor.storage.suggestionMode.enabled = enabled;
}
